// redux/slices/salonMastersSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";

// 🔐 централизованный JWT-клиент
import { apiRequest } from "../../Utils/apiClient";
import { clearJwtToken } from "../../Utils/authStorage";

const initialState = {
  masters: [],
  employees: [],
  selectedMaster: null,
  selectedMasterAppointments: [],
  allMastersAppointments: {}, // { [masterUid]: Appointment[] }
  viewMode: "single",         // 'single' | 'all'
  status: "idle",             // загрузка мастеров / bundle
  apptStatus: "idle",         // загрузка расписаний
  error: null,
};

const normalizeRole = (role) => String(role || "").toLowerCase();
const isMaster = (role) => normalizeRole(role) === "master";
const isEmployee = (role) => normalizeRole(role) === "employee";

const dedupByUid = (arr = []) => {
  const map = new Map();
  for (const p of arr) {
    if (!p?.uid) continue;
    if (!map.has(p.uid)) map.set(p.uid, p);
  }
  return [...map.values()];
};

/* ======================= Thunks ======================= */

// GET /salon/:ownerUid/masters (PRIVATE, JWT)
export const fetchSalonMasters = createAsyncThunk(
  "salonMasters/fetchSalonMasters",
  async ({ ownerUid }, { rejectWithValue }) => {
    try {
      if (!ownerUid) throw new Error("ownerUid is required");

      const res = await apiRequest(
        "GET",
        `/salon/${encodeURIComponent(ownerUid)}/masters`
      );

      const data = res?.data;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      const msg =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch masters";
      return rejectWithValue(msg);
    }
  }
);

// GET /salon/:ownerUid/appointments/:masterUid?date=YYYY-MM-DD (PRIVATE, JWT)
export const fetchMasterAppointments = createAsyncThunk(
  "salonMasters/fetchMasterAppointments",
  async ({ ownerUid, masterUid, date }, { rejectWithValue }) => {
    try {
      if (!ownerUid || !masterUid) {
        throw new Error("ownerUid and masterUid are required");
      }

      const qsDate = date ? `?date=${encodeURIComponent(date)}` : "";
      const path = `/salon/${encodeURIComponent(
        ownerUid
      )}/appointments/${encodeURIComponent(masterUid)}${qsDate}`;

      const res = await apiRequest("GET", path);
      const data = res?.data;

      return {
        masterUid,
        appointments: Array.isArray(data) ? data : [],
      };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      const msg =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch appointments";
      return rejectWithValue(msg);
    }
  }
);

// GET /salon/:ownerUid/appointments?date=YYYY-MM-DD&uids=uid1,uid2,... (PRIVATE, JWT)
// Возвращает [{ masterUid, appointments: [...] }, ...]
export const fetchAllMastersAppointments = createAsyncThunk(
  "salonMasters/fetchAllMastersAppointments",
  async ({ ownerUid, masterUids = [], date }, { rejectWithValue }) => {
    try {
      if (!ownerUid) throw new Error("ownerUid is required");

      const uids = (masterUids || []).filter(Boolean);
      if (!uids.length) return [];

      // Разбиваем по 100 на запрос
      const chunks = [];
      for (let i = 0; i < uids.length; i += 100) {
        chunks.push(uids.slice(i, i + 100));
      }

      const results = [];

      for (const chunk of chunks) {
        const params = new URLSearchParams();
        if (date) params.append("date", date);
        params.append("uids", chunk.join(","));

        const path = `/salon/${encodeURIComponent(
          ownerUid
        )}/appointments?${params.toString()}`;

        const res = await apiRequest("GET", path);
        const data = res?.data;

        if (Array.isArray(data)) results.push(...data);
      }

      return results; // [{ masterUid, appointments }]
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      const msg =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch appointments (batch)";
      return rejectWithValue(msg);
    }
  }
);

// GET /salon/:ownerUid/bundle?date=YYYY-MM-DD (PRIVATE, JWT)
// Ответ: { masters: [...], schedules: { [uid]: Appointment[] }, tookMs }
export const fetchSalonBundle = createAsyncThunk(
  "salonMasters/fetchSalonBundle",
  async ({ ownerUid, date }, { rejectWithValue }) => {
    try {
      if (!ownerUid) throw new Error("ownerUid is required");

      const params = new URLSearchParams();
      if (date) params.append("date", date);

      const path = `/salon/${encodeURIComponent(
        ownerUid
      )}/bundle?${params.toString()}`;

      const res = await apiRequest("GET", path);
      const data = res?.data || {};

      const masters = Array.isArray(data?.masters) ? data.masters : [];
      const schedules =
        data?.schedules && typeof data.schedules === "object"
          ? data.schedules
          : {};

      return { masters, schedules };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        try { await clearJwtToken(); } catch { }
      }
      const msg =
        error?.response?.data ||
        error?.message ||
        "Failed to fetch salon bundle";
      return rejectWithValue(msg);
    }
  }
);

/* ======================= Slice ======================= */

const salonMastersSlice = createSlice({
  name: "salonMasters",
  initialState,
  reducers: {
    setSelectedMaster: (state, action) => {
      state.selectedMaster = action.payload || null;
      const uid = state.selectedMaster?.uid;
      state.selectedMasterAppointments = uid
        ? state.allMastersAppointments[uid] || []
        : [];
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload === "all" ? "all" : "single";
    },
    clearSelectedMaster: (state) => {
      state.selectedMaster = null;
      state.selectedMasterAppointments = [];
    },
    resetSalonMasters: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /* ---------- список мастеров ---------- */
      .addCase(fetchSalonMasters.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchSalonMasters.fulfilled, (state, action) => {
        state.status = "succeeded";
        const list = dedupByUid(action.payload);
        state.masters = list.filter((p) => isMaster(p.role));
        state.employees = list.filter((p) => isEmployee(p.role));
      })
      .addCase(fetchSalonMasters.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch masters";
      })

      /* ---------- bundle: мастера + расписания ---------- */
      .addCase(fetchSalonBundle.pending, (state) => {
        state.status = "loading";
        state.apptStatus = "loading";
        state.error = null;
      })
      .addCase(fetchSalonBundle.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.apptStatus = "succeeded";

        const list = dedupByUid(action.payload.masters || []);
        state.masters = list.filter((p) => isMaster(p.role));
        state.employees = list.filter((p) => isEmployee(p.role));

        const schedulesObj = action.payload.schedules || {};
        state.allMastersAppointments = {};
        for (const [uid, items] of Object.entries(schedulesObj)) {
          state.allMastersAppointments[uid] = Array.isArray(items) ? items : [];
        }

        if (state.selectedMaster?.uid) {
          state.selectedMasterAppointments =
            state.allMastersAppointments[state.selectedMaster.uid] || [];
        }
      })
      .addCase(fetchSalonBundle.rejected, (state, action) => {
        state.status = "failed";
        state.apptStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch salon bundle";
      })

      /* ---------- расписание одного мастера ---------- */
      .addCase(fetchMasterAppointments.pending, (state) => {
        state.apptStatus = "loading";
      })
      .addCase(fetchMasterAppointments.fulfilled, (state, action) => {
        state.apptStatus = "succeeded";
        const { masterUid, appointments } = action.payload || {};
        if (!masterUid) return;
        state.allMastersAppointments[masterUid] = Array.isArray(appointments)
          ? appointments
          : [];
        if (state.selectedMaster?.uid === masterUid) {
          state.selectedMasterAppointments =
            state.allMastersAppointments[masterUid];
        }
      })
      .addCase(fetchMasterAppointments.rejected, (state, action) => {
        state.apptStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch appointments";
      })

      /* ---------- расписание нескольких мастеров (батч) ---------- */
      .addCase(fetchAllMastersAppointments.pending, (state) => {
        state.apptStatus = "loading";
      })
      .addCase(fetchAllMastersAppointments.fulfilled, (state, action) => {
        state.apptStatus = "succeeded";
        for (const { masterUid, appointments } of action.payload || []) {
          state.allMastersAppointments[masterUid] = Array.isArray(appointments)
            ? appointments
            : [];
          if (state.selectedMaster?.uid === masterUid) {
            state.selectedMasterAppointments =
              state.allMastersAppointments[masterUid];
          }
        }
      })
      .addCase(fetchAllMastersAppointments.rejected, (state, action) => {
        state.apptStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "Failed to fetch appointments (batch)";
      })

      /* ---------- глобальный reset ---------- */
      .addCase(resetStore, () => initialState);
  },
});

export const {
  setSelectedMaster,
  setViewMode,
  clearSelectedMaster,
  resetSalonMasters,
} = salonMastersSlice.actions;

export default salonMastersSlice.reducer;

/* ======================= Selectors ======================= */
export const selectMasters = (s) => s.salonMasters.masters;
export const selectEmployees = (s) => s.salonMasters.employees;
export const selectSelectedMaster = (s) => s.salonMasters.selectedMaster;
export const selectSelectedMasterAppointments = (s) =>
  s.salonMasters.selectedMasterAppointments;
export const selectAppointmentsByMaster = (s, masterUid) =>
  s.salonMasters.allMastersAppointments?.[masterUid] || [];
export const selectSalonMastersStatus = (s) => s.salonMasters.status;
export const selectSalonApptStatus = (s) => s.salonMasters.apptStatus;
export const selectSalonError = (s) => s.salonMasters.error;
export const selectViewMode = (s) => s.salonMasters.viewMode;
